import { Box, Input, Typography, Stack } from '@mui/joy';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import { SearchBar } from '@/ui/SearchBar';
import { SelectableButton } from '@/components/SelectableButton';
import { capitalizeFirstLetter } from '@/utils/strings';

// Chips wrap, so a row holds however many happen to fit and each is as wide as
// its label. Virtualizing that means working out the line breaks ourselves:
// estimate each chip, pack them into rows the way wrapping would, and
// virtualize the rows.
//
// The rule that keeps this stable: nothing measured from the packed chips is
// ever fed back into state. What the layout depends on is read off two
// invisible probe chips that are always present and never change, so measuring
// twice always gives the same answer, and each label's width is worked out
// during render only. Measure the real chips instead and the packing decides
// what gets measured while the measurement decides the packing -- the two then
// chase each other until React gives up with "Maximum update depth exceeded".
const CHIP_GAP = 6;
const MAX_LABEL_WIDTH = 200;
const PROBE_LABEL = 'Probe';
const FALLBACK_ROW_HEIGHT = 44;

export function ChipMultiSelect({ value, onChange, options, entity, size = 'md' }) {
  const [search, setSearch] = useState('');
  const scrollRef = useRef(null);

  const filteredOptions = useMemo(() => {
    const query = search.trim().toLowerCase();
    return options.filter(({ name }) => !query || name.toLowerCase().includes(query));
  }, [options, search]);

  const sampleIcon = useMemo(() => options.find((option) => option.icon)?.icon, [options]);
  const layout = useChipLayout(scrollRef, Boolean(sampleIcon));

  const rows = useMemo(() => packRows(filteredOptions, layout), [filteredOptions, layout]);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => layout.rowHeight,
    overscan: 3,
  });

  const toggle = (name) => {
    onChange(value.includes(name) ? value.filter((n) => n !== name) : [...value, name]);
  };

  const handleSearch = (value) => {
    setSearch(value);
    // Show the new results from the top rather than from wherever the previous
    // list happened to be scrolled to.
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  };

  return (
    <Box> 
      <SearchBar
        value={search}
        onChange={handleSearch}
        entity={entity}
        size={size}
        sx={{ mb: 1.25 }}
      />
      <Box
        ref={scrollRef}
        sx={{
          maxHeight: 220,
          overflowY: 'auto',
          // Reserve the scrollbar always, so the width the chips wrap inside
          // does not change the moment the list grows long enough to scroll.
          scrollbarGutter: 'stable',          
          position: 'relative',
        }}
      >
        <ChipProbes icon={sampleIcon} />

        {filteredOptions.length === 0 ? (
          <Typography level="body-xs" textColor="text.tertiary" sx={{ mt: 0.5 }}>No {entity} matches &quot;{search}&quot;</Typography>
        ) : (
          <Box sx={{ height: virtualizer.getTotalSize(), width: '100%', position: 'relative' }}>
            {virtualizer.getVirtualItems().map((virtualRow) => (
              <Box
                key={virtualRow.key}
                data-index={virtualRow.index}
                ref={virtualizer.measureElement}
                // The offset goes through `style`: it changes on every scroll
                // frame and `sx` would serialize a class per distinct value.
                style={{ transform: `translateY(${virtualRow.start}px)` }}
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: `${CHIP_GAP}px`,
                  pb: `${CHIP_GAP}px`,
                }}
              >
                {rows[virtualRow.index].map((option) => (
                  <Chip
                    key={option.name}
                    option={option}
                    selected={value.includes(option.name)}
                    onClick={() => toggle(option.name)}
                  />
                ))}
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

// Options may carry their own label; tags and categories are stored lowercase
// and just get capitalized for display.
function chipLabel({ name, label }) {
  return label ?? capitalizeFirstLetter(name);
}

function Chip({ option, selected, onClick, probe }) {
  const { icon } = option;
  const label = chipLabel(option);

  return (
    <SelectableButton onClick={onClick} selected={selected}>
      <Box
        component="span"
        data-chip-probe={probe}
        sx={{ display: 'block', maxWidth: MAX_LABEL_WIDTH, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', ml: (icon) ? -0.25 : 0 }}
      >
        {icon ? <Stack direction="row" gap={1} alignItems="center">
          {icon}
          {label}
        </Stack> : label}
      </Box>
    </SelectableButton>
  );
}

// Two chips carrying the same label, one with an icon and one without, parked
// out of sight so they can be measured. The layout is read from these rather
// than from the list: unlike the chips being packed, these are always there
// and always identical.
function ChipProbes({ icon }) {
  return (
    <Box
      aria-hidden
      sx={{ position: 'absolute', top: 0, left: 0, height: 0, overflow: 'hidden', visibility: 'hidden', pointerEvents: 'none', display: 'flex' }}
    >
      <Chip option={{ name: PROBE_LABEL }} probe="plain" />
      {icon && <Chip option={{ name: PROBE_LABEL, icon }} probe="icon" />}
    </Box>
  );
}

const EMPTY_LAYOUT = { width: 0, font: null, padding: 0, iconExtra: 0, rowHeight: FALLBACK_ROW_HEIGHT };

// Everything the packing needs, all of it read off the probes and the
// container -- never off the chips being packed.
function useChipLayout(scrollRef, hasIcons) {
  const [layout, setLayout] = useState(EMPTY_LAYOUT);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    const update = () => setLayout((current) => {
      const next = readLayout(element);
      return next && !sameLayout(current, next) ? next : current;
    });

    update();
    const observer = new ResizeObserver(update);
    observer.observe(element);
    // A webfont landing changes every label width, so read it again once the
    // fonts the page asked for have arrived.
    document.fonts?.ready.then(update);
    return () => observer.disconnect();
  }, [scrollRef, hasIcons]);

  return layout;
}

function readLayout(element) {
  const plain = element.querySelector('[data-chip-probe="plain"]');
  if (!plain) return null;

  const plainChip = plain.closest('button');
  const iconChip = element.querySelector('[data-chip-probe="icon"]')?.closest('button');
  const labelStyle = getComputedStyle(plain);
  const boxStyle = getComputedStyle(element);
  const widthOf = (node) => node.getBoundingClientRect().width;

  return {
    width: element.clientWidth - parseFloat(boxStyle.paddingLeft) - parseFloat(boxStyle.paddingRight),
    font: `${labelStyle.fontWeight} ${labelStyle.fontSize} ${labelStyle.fontFamily}`,
    // The label box holds the icon as well as the text, so what is left over
    // is the button's own padding and border.
    padding: widthOf(plainChip) - widthOf(plain),
    // Same label either way, so the difference is exactly what an icon adds.
    iconExtra: iconChip ? widthOf(iconChip) - widthOf(plainChip) : 0,
    rowHeight: plainChip.getBoundingClientRect().height + CHIP_GAP,
  };
}

function sameLayout(a, b) {
  return a.font === b.font
    && Math.abs(a.width - b.width) < 1
    && Math.abs(a.padding - b.padding) < 1
    && Math.abs(a.iconExtra - b.iconExtra) < 1
    && Math.abs(a.rowHeight - b.rowHeight) < 1;
}

// The same greedy line-breaking wrapping does: keep adding chips until the
// next one would not fit, then start a row.
function packRows(options, layout) {
  if (options.length === 0 || !layout.width || !layout.font) return [];

  const measure = textMeasurer(layout.font);
  const rows = [];
  let row = [];
  let used = 0;

  for (const option of options) {
    const width = Math.min(chipWidth(option, layout, measure), layout.width);
    const needed = row.length === 0 ? width : used + CHIP_GAP + width;

    if (row.length > 0 && needed > layout.width) {
      rows.push(row);
      row = [option];
      used = width;
    } else {
      row.push(option);
      used = needed;
    }
  }

  rows.push(row);
  return rows;
}

function chipWidth(option, layout, measure) {
  const label = Math.min(measure(chipLabel(option)), MAX_LABEL_WIDTH);
  // Rounded up so an estimate never lands under the real chip: a row with a
  // little space left at the end looks fine, one that overflows wraps.
  return Math.ceil(label) + layout.padding + (option.icon ? layout.iconExtra : 0);
}

// Measuring text on a canvas keeps this off the DOM entirely -- putting a node
// on the page per option is the cost we are here to avoid. It is only ever
// called while rendering, never to decide state, so a canvas that measures
// slightly differently can nudge where a row breaks but cannot start a loop.
let measuringContext = null;

function textMeasurer(font) {
  if (!measuringContext) {
    measuringContext = document.createElement('canvas').getContext('2d');
  }
  measuringContext.font = font;
  return (text) => measuringContext.measureText(text).width;
}
